/*
,--.   ,--.,--. ,--.,------.,--.   ,--.
 \  `.'  / |  | |  ||  .---' \  `.'  /
  \     /  |  | |  ||  `--,   .'    \
   \   /   '  '-'  '|  `---. /  .'.  \
    `-'     `-----' `------''--'   '--'

VUEX RESPONSIBLE FOR THE UI RELATED VARIABLES, SUCH AS STATES
*/
/// ///////////////////////////////////////////////////////////////////////////////////////

/*
,--.  ,--.,------.,--.   ,------. ,------.,------.
|  '--'  ||  .---'|  |   |  .--. '|  .---'|  .--. '
|  .--.  ||  `--, |  |   |  '--' ||  `--, |  '--'.'
|  |  |  ||  `---.|  '--.|  | --' |  `---.|  |\  \
`--'  `--'`------'`-----'`--'     `------'`--' '--'
 ,---. ,--------.,------. ,--.,--.  ,--. ,----.
'   .-''--.  .--'|  .--. '|  ||  ,'.|  |'  .-./
`.  `-.   |  |   |  '--'.'|  ||  |' '  ||  | .---.
.-'    |  |  |   |  |\  \ |  ||  | `   |'  '--'  |
`-----'   `--'   `--' '--'`--'`--'  `--' `------'
*/

/// ///////////////////////////////////////////////////////////////////////////////////////

/*
 ,---.  ,------.,--------.,--. ,--.,------.
'   .-' |  .---''--.  .--'|  | |  ||  .--. '
`.  `-. |  `--,    |  |   |  | |  ||  '--' |
.-'    ||  `---.   |  |   '  '-'  '|  | --'
`-----' `------'   `--'    `-----' `--'
*/
/* eslint no-return-await: 0 */

import Vue from 'vue'
import Vuex from 'vuex'
import localDB from './localDB'
import settings from './settings'
import welcome from './welcome'
import userPermission from './userPermission.js'

/*eslint-disable */
const CONSOLE_TAG_GREEN = 'color: white; background-color: #2ecc71; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
const CONSOLE_TAG_ORANGE = 'color: white; background-color: #f39c12; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
const CONSOLE_TAG_RED = 'color: white; background-color: #e74c3c; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
const CONSOLE_TAG_BLUE = 'color: white; background-color: #3498db; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
const AUTHOR_ICON = 'display: block; height: 100px; width: 100px; background: url(https://i.postimg.cc/qtv4ypzj/photo-2019-04-02-15-25-43.jpg) no-repeat; font-size:150px;'
/* eslint-enable */

const localStore = window.localStorage

Vue.use(Vuex)

const IS_PRODUCTION = settings.production
if (IS_PRODUCTION) {
  console.log(welcome.console)
  console.warn('!!! DO NOT DEV OR TEST UNDER PRODUCTION MODE !!!')
}

// listen to event from firebase service or local db
document.addEventListener('FIRE-SERVICE-EVENT-TO-VUEX', handleIncomingEvent)
document.addEventListener('LOCAL-DB-EVENT-TO-VUEX', handleIncomingEvent)

export var store = new Vuex.Store({
  state: {
    // app data
    networkStatus: window.navigator.onLine,
    forceOfflineMode: JSON.parse(localStore.getItem('forceOfflineMode')) || settings.forceOfflineOnInit,
    initializing: false,

    // user sign data
    signing: false,
    currentUser: null,
    signError: '',
    signSuccess: '',

    // item editor data
    showItemEditor: false,
    editingItem: null,

    // real time data
    projects: [],
    formTemplates: [],
    formQuestions: [],
    users: [],

    // project home data
    currentProject: null,
    currentFolder: null,
    currentFolders: [],
    currentItems: [],
    currentOfflineFolders: [],
    currentOfflineItems: [],
    projectLoading: false,
    itemsLoading: false,
    foldersLoading: false
  },
  getters: {
    onLine: function (state) {
      return state.networkStatus && !state.forceOfflineMode
    },
    currentUserInfo: function (state) {
      if (!state.currentUser) {
        return null
      }
      return {
        uid: state.currentUser.uid,
        name: state.currentUser.name,
        email: state.currentUser.email,
        status: state.currentUser.status,
        scope: state.currentUser.userScope,
        permits: userPermission.getPermits(state.currentUser.userScope)
      }
    },
    fullOfflineItemList: function (state) {
      return state.currentOfflineItems.filter(item => {
        return item.open
      })
    },
    fullItemList: function (state) {
      return state.currentItems.filter(item => {
        return item.open
      })
    },
    itemList: function (state) {
      return calcItemList()
    },
    offlineItemList: function (state) {
      return calcOfflineItemList()
    },
    nextItemIndex: function (state) {
      return calcNextItemIndex()
    },
    currentProject: function (state) {
      return state.currentProject
    }
  },
  mutations: {
    updateNetworkStatus: function (state, data) {
      if (!data) {
        // console.warn('Device offline')
      }
      state.networkStatus = data
    },
    updateForceOfflineMode: function (state, data) {
      state.forceOfflineMode = data
    },
    updateCurrentUser: function (state, data) {
      // console.log('update user =>', data)
      state.currentUser = data
      console.log('%c User ', CONSOLE_TAG_BLUE, 'New user state =>', data)
    },
    startSign: function (state) {
      // clear the error and active the sign loader
      state.signing = true
      state.signError = ''
    },
    endSign: function (state, error) {
      // display the error and inactive the sign loader
      state.signing = false
      state.signError = error || ''
    },
    updateProjects: function (state, list) {
      state.projects = list
      console.log('%c Data : Projects ', CONSOLE_TAG_BLUE, `Updated ${list.length} projects in App state.`)
      console.log('%c Data : Projects ', CONSOLE_TAG_GREEN, 'Update success')
      console.groupEnd()
    },
    updateFormTemplates: function (state, list) {
      state.formTemplates = list
      console.log('%c Data : Templates ', CONSOLE_TAG_BLUE, `Updated ${list.length} templates in App state.`)
      console.log('%c Data : Templates ', CONSOLE_TAG_GREEN, 'Update success')
      console.groupEnd()
    },
    updateFormQuestions: function (state, list) {
      state.formQuestions = list
      console.log('%c Data : Questions ', CONSOLE_TAG_BLUE, `Updated ${list.length} questions in App state.`)
      console.log('%c Data : Questions ', CONSOLE_TAG_GREEN, 'Update success')
      console.groupEnd()
    },
    updateUsers: function (state, list) {
      state.users = list
      console.log('%c Data : Users ', CONSOLE_TAG_BLUE, `Updated ${list.length} users in App state.`)
      console.log('%c Data : Users ', CONSOLE_TAG_GREEN, 'Update success')
      console.groupEnd()
    },
    startSwitchProject: function (state) {
      state.projectLoading = true
      state.foldersLoading = true
      state.itemsLoading = true
    },
    updateCurrentProject: function (state, data) {
      state.projectLoading = false
      state.currentProject = data
    },
    updateCurrentFolder: function (state, data) {
      state.currentFolder = data
    },
    updateCurrentFolders: function (state, data) {
      state.foldersLoading = false
      state.currentFolders = data
      if (state.currentFolder && state.currentFolder.id) {
        let currentFolder = state.currentFolder
        state.currentFolder = data.find(folder => { return folder.id === currentFolder.id }) || null
      }
    },
    updateCurrentItems: function (state, data) {
      state.itemsLoading = false
      state.currentItems = data
    },
    updateCurrentOfflineFolders: function (state, data) {
      state.currentOfflineFolders = data
    },
    updateCurrentOfflineItems: function (state, data) {
      state.currentOfflineItems = data
    },
    setEditingItem: function (state, data) {
      state.editingItem = data
      state.showItemEditor = !!data
    }
  },
  actions: {
    initApp: async function (context) {
      context.state.initializing = true
      await localDB.initApp()
      context.state.initializing = false
    },
    signUpUser: async function (context, signUpRequest) {
      context.commit('startSign')
      let signUpResult = await localDB.signUpUser(signUpRequest)
      if (signUpResult.success) {
        context.commit('endSign')
      } else {
        context.commit('endSign', signUpResult.error)
      }
    },
    signInUser: async function (context, signInRequest) {
      context.commit('startSign')
      let signInResult = await localDB.authSignIn(signInRequest)
      if (signInResult.success) {
        context.commit('endSign')
      } else {
        context.commit('endSign', signInResult.error)
      }
    },
    signOutUser: function (context) {
      localDB.authSignOut()
    },
    resetPassword: async function (context, email) {
      context.commit('startSign')
      let resetSentResult = await localDB.resetPassword(email)
      if (resetSentResult.success) {
        context.state.signSuccess = resetSentResult.message
        context.commit('endSign')
      } else {
        context.commit('endSign', resetSentResult.error)
      }
    },
    switchProject: async function (context, projectId) {
      if (!context.getters.currentUserInfo.permits.PROJECT_VIEW) {
        return {
          success: false,
          error: 'You have no permit to VIEW PROJECT DATA'
        }
      }
      // console.log('Switching to project =>', projectId)
      if (!projectId) {
        console.warn('Invalid project ID =>', projectId)
        return {
          success: false,
          error: 'Invalid project ID'
        }
      }
      context.commit('startSwitchProject')
      store.commit('updateCurrentOfflineFolders', await localDB.getOfflineFoldersOffline(projectId))
      return await localDB.switchProject(projectId)
    },
    addProject: async function (context, projectInfo) {
      if (!context.getters.currentUserInfo.permits.PROJECT_CREATE) {
        return {
          success: false,
          error: 'You have no permit to CREATE NEW PROJECT'
        }
      }
      projectInfo.status = 'MEASUREMENT'
      projectInfo.handlingFee = '0.00'
      let addResult = await localDB.addProject(projectInfo)
      if (addResult.success) {
        return {
          success: true,
          error: ''
        }
      } else {
        return {
          success: false,
          error: addResult.error
        }
      }
    },
    changeProjectSetting: async function (context, project) {
      if (!context.getters.currentUserInfo.permits.PROJECT_EDIT) {
        return {
          success: false,
          error: 'You have no permit to CHANGE PROJECT SETTING'
        }
      }
      if (typeof project !== 'object') {
        return {
          success: false,
          error: 'No project data'
        }
      }
      if (!context.getters.onLine) {
        return {
          success: false,
          error: 'Cannot save changes while offline'
        }
      }
      return await localDB.changeProjectSetting(project)
    },
    addNewFolder: async function (context, folderName) {
      if (!context.getters.currentUserInfo.permits.FOLDER_CREATE) {
        return {
          success: false,
          error: 'You have no permit to CREATE FOLDER'
        }
      }
      if (context.state.currentProject.status !== 'MEASUREMENT' && !context.getters.currentUserInfo.permits.FOLDER_FORCE_CREATE) {
        return {
          success: false,
          error: 'You can add folder in measurement stage only'
        }
      }
      if (typeof folderName !== 'string') {
        return {
          success: false,
          error: 'Invalid Folder Name'
        }
      }
      if (context.state.currentProject === null) {
        return {
          success: false,
          error: 'Current Project Not Set'
        }
      }
      let newFolder = {
        id: localDB.getRandomId('folders'),
        name: folderName,
        images: [],
        projectId: context.state.currentProject.id,
        createOn: Date.now(),
        createBy: {
          uid: context.getters.currentUserInfo.uid,
          name: context.getters.currentUserInfo.name
        },
        open: true
      }
      return await localDB.setFolder(newFolder)
    },
    enableOfflineMode: function (context) {
      localDB.enableOfflineMode()
    },
    disableOfflineMode: function (context) {
      localDB.disableOfflineMode()
    },
    setFolder: async function (context, folder) {
      if (!context.getters.currentUserInfo.permits.FOLDER_EDIT) {
        return {
          success: false,
          error: 'You have no permit to EDIT FOLDER'
        }
      }
      if (context.state.currentProject.status !== 'MEASUREMENT' && !context.getters.currentUserInfo.permits.FOLDER_FORCE_EDIT) {
        return {
          success: false,
          error: 'You can edit folder in measurement stage only'
        }
      }
      return await localDB.setFolder(folder)
    },
    addNewItem: async function (context, addItemRequest) {
      /*
      addItemRequest{
        templateId
        newItemName
        folderId
      }
      */
      if (!context.getters.currentUserInfo.permits.ITEM_CREATE) {
        return {
          success: false,
          error: 'You have no permit to CREATE ITEM'
        }
      }
      if (context.state.currentProject.status !== 'MEASUREMENT' && !context.getters.currentUserInfo.permits.ITEM_FORCE_CREATE) {
        return {
          success: false,
          error: 'You can add item in measurement stage only'
        }
      }
      try {
        let newItem = await createNewItem(addItemRequest)
        return await localDB.setItem(newItem)
      } catch (error) {
        console.warn(error)
        return {
          success: false,
          error: error
        }
      }
    },
    editItem: async function (context, itemId) {
      let item = await localDB.getItemData(itemId)
      // console.log(item)
      if (!item) {
        return {
          success: false,
          error: 'Item not exists or deleted'
        }
      }
      if (!context.getters.currentUserInfo.permits.ITEM_EDIT) {
        return {
          success: false,
          error: 'You have no permit to EDIT ITEM'
        }
      }
      if (context.state.currentProject.status !== 'MEASUREMENT' && !context.getters.currentUserInfo.permits.ITEM_FORCE_EDIT) {
        return {
          success: false,
          error: 'You can edit item in measurement stage only'
        }
      }
      context.commit('setEditingItem', item)
      return {
        success: true,
        error: ''
      }
    },
    uploadImage: async function (context, file) {
      return localDB.uploadImage(file)
    },
    saveItemChange: async function (context, saveItemRequest) {
      /*
        saveItemRequest: {
          itemToSave: Object,
          nextItem: null / '' / 'New Item Name'  // null => close; '' => save and blank; 'New Item Name'
        }
      */
      let stamp = Date.now()
      let user = {
        uid: context.getters.currentUserInfo.uid,
        name: context.getters.currentUserInfo.name
      }
      saveItemRequest.itemToSave.lastEditBy = user
      saveItemRequest.itemToSave.lastEditOn = stamp
      let saveEditedItemResult = await localDB.setItem(saveItemRequest.itemToSave)
      if (!saveEditedItemResult.success) {
        return {
          success: false,
          error: saveEditedItemResult.error
        }
      }
      if (saveItemRequest.nextItem === null) {
        // save and close
        context.commit('setEditingItem', null)
        return {
          success: true,
          error: ''
        }
      } else if (saveItemRequest.nextItem === '') {
        // save and copy
        let stamp = Date.now()
        let user = {
          uid: context.getters.currentUserInfo.uid,
          name: context.getters.currentUserInfo.name
        }
        let newItem = saveItemRequest.itemToSave
        newItem.id = localDB.getRandomId('items')
        newItem.images = [] // NOTE: comment this LINE if customer want to copy the image
        newItem.createOn = stamp
        newItem.createBy = user
        newItem.lastEditOn = null
        newItem.lastEditBy = null
        newItem.open = true
        newItem.version = 0
        newItem.index = context.getters.nextItemIndex
        for (let question of newItem.questions) {
          // clear the number less than 0 part for width and height
          if (question.id === 'numop_orderHeight' || question.id === 'numop_orderWidth') {
            question.value[1] = '0'
          }
        }
        let saveCopyItemResult = await localDB.setItem(newItem)
        if (saveCopyItemResult.success) {
          context.commit('setEditingItem', newItem)
          return {
            success: true,
            error: ''
          }
        } else {
          return {
            success: false,
            error: saveCopyItemResult.error
          }
        }
      } else if (typeof saveItemRequest.nextItem === 'string') {
        // save and new
        let addItemRequest = {
          templateId: saveItemRequest.itemToSave.templateId,
          newItemName: saveItemRequest.nextItem,
          folderId: saveItemRequest.itemToSave.folderId,
          RF: ''
        }
        let addNewItemResult = await context.dispatch('addNewItem', addItemRequest)
        if (addNewItemResult.success) {
          context.commit('setEditingItem', addNewItemResult.item)
          return {
            success: true,
            error: ''
          }
        } else {
          return {
            success: true,
            error: addNewItemResult.error
          }
        }
      }
    },
    deleteItem: async function (context, item) {
      if (!context.getters.currentUserInfo.permits.ITEM_DELETE) {
        return {
          success: false,
          error: 'You have no permit to DELETE ITEM'
        }
      }
      if (context.state.currentProject.status !== 'MEASUREMENT' && !context.getters.currentUserInfo.permits.ITEM_FORCE_DELETE) {
        return {
          success: false,
          error: 'You can delete item in measurement stage only'
        }
      }
      let stamp = Date.now()
      let user = {
        uid: context.getters.currentUserInfo.uid,
        name: context.getters.currentUserInfo.name
      }
      item.lastEditBy = user
      item.lastEditOn = stamp
      item.open = false
      return await localDB.setItem(item)
    },
    closeItemEditor: function (context) {
      context.commit('setEditingItem', null)
    },
    inviteUser: async function (context, email) {
      if (!context.getters.currentUserInfo.permits.USER_INVITATION) {
        return {
          success: false,
          error: 'You have no permit to SEND USER INVITATION'
        }
      }
      return await localDB.sendInvitationEmail(email)
    },
    changeUserStatus: async function (context, changeRequest) {
      // changeRequest : {
      //   newStatus: 'ACTIVE/HOLD',
      //   uid: 'ADDSKFHAFIHALFKASHDLFK'
      // }
      if (!context.getters.currentUserInfo.permits.USER_STATUS_EDIT) {
        return {
          success: false,
          error: 'You have no permit to CHANGE USER STATUS'
        }
      }
      let operator = {
        uid: context.getters.currentUserInfo.uid,
        name: context.getters.currentUserInfo.name
      }
      let userStatusChangeRequest = {
        uid: changeRequest.uid,
        newStatus: changeRequest.newStatus,
        operator: operator
      }
      return await localDB.changeUserStatus(userStatusChangeRequest)
    },
    changeUserScope: async function (context, changeRequest) {
      // changeRequest : {
      //   newScope: '2,3,4',
      //   uid: 'ADDSKFHAFIHALFKASHDLFK'
      // }
      if (!context.getters.currentUserInfo.permits.USER_SCOPE_EDIT) {
        return {
          success: false,
          error: 'You have no permit to CHANGE USER ROLE'
        }
      }
      let operator = {
        uid: context.getters.currentUserInfo.uid,
        name: context.getters.currentUserInfo.name
      }
      let userScopeChangeRequest = {
        uid: changeRequest.uid,
        newScope: changeRequest.newScope,
        operator: operator
      }
      return await localDB.changeUserScope(userScopeChangeRequest)
    }
  }
})

/// ///////////////////////////////////////////////////////////////////////////////////////
/*
,--.  ,--.,------.,--.   ,------. ,------.,------.
|  '--'  ||  .---'|  |   |  .--. '|  .---'|  .--. '
|  .--.  ||  `--, |  |   |  '--' ||  `--, |  '--'.'
|  |  |  ||  `---.|  '--.|  | --' |  `---.|  |\  \
`--'  `--'`------'`-----'`--'     `------'`--' '--'

,------.,--. ,--.,--.  ,--. ,-----.,--------.,--. ,-----. ,--.  ,--.
|  .---'|  | |  ||  ,'.|  |'  .--./'--.  .--'|  |'  .-.  '|  ,'.|  |
|  `--, |  | |  ||  |' '  ||  |       |  |   |  ||  | |  ||  |' '  |
|  |`   '  '-'  '|  | `   |'  '--'\   |  |   |  |'  '-'  '|  | `   |
`--'     `-----' `--'  `--' `-----'   `--'   `--' `-----' `--'  `--'

*/
async function handleIncomingEvent (event) {
  let payload = event.detail
  // console.log('receive incoming event => ', payload)
  switch (payload.event) {
    case 'hello':
      // console.log(payload.data)
      break
    case 'deviceNetworkChange':
      store.commit('updateNetworkStatus', payload.data)
      break
    case 'forceOfflineModeChanged':
      store.commit('updateForceOfflineMode', payload.data)
      break
    case 'currentUser':
      store.commit('updateCurrentUser', payload.data)
      break
    case 'updateProjects':
      store.commit('updateProjects', payload.data)
      break
    case 'updateFormTemplates':
      store.commit('updateFormTemplates', payload.data)
      break
    case 'updateFormQuestions':
      store.commit('updateFormQuestions', payload.data)
      break
    case 'updateUsers':
      store.commit('updateUsers', payload.data)
      break
    case 'updateCurrentProject':
      store.commit('updateCurrentProject', payload.data)
      break
    case 'updateCurrentFolders':
      store.commit('updateCurrentFolders', payload.data)
      break
    case 'updateCurrentItems':
      store.commit('updateCurrentItems', payload.data)
      break
    case 'refreshCurrentOfflineFolders':
      store.commit('updateCurrentOfflineFolders', await localDB.getOfflineFoldersOffline(store.state.currentProject))
      break
    case 'refreshCurrentOfflineItems':
      store.commit('updateCurrentOfflineItems', await localDB.getOfflineItemsOffline(store.state.currentProject))
      break
    default:
      console.warn('Unknown event name =>', payload.event)
  }
}
/*eslint-disable */
function parseAuthErrorCode (error) {
  switch (error) {
    case 'auth/user-disabled':
      return 'This account has been disabled'
    case 'auth/invalid-email':
      return 'Invalid email address'
    case 'auth/user-not-found':
      return 'No user found for this email address'
    case 'auth/wrong-password':
      return 'Incorrect email address or password'
    case 'auth/email-already-in-use':
      return 'Email address already exists'
    case 'auth/weak-password':
      return 'Password too weak'
    default:
      return 'Unable to contact authentication server'
  }
}
/* eslint-enable */

async function createNewItem (addItemRequest) {
  /*
  addItemRequest{
    templateId
    newItemName
    folderId
  }
  */
  let formTemplate = store.state.formTemplates.find(template => { return template.templateId === addItemRequest.templateId })
  if (formTemplate === undefined) {
    console.warn('Unknown templateId =>', addItemRequest.templateId)
    throw new Error('Template not available')
  }
  if (!store.state.currentProject.id) {
    throw new Error('This project has been closed')
  }
  // assemble the item instance meta data
  let stamp = Date.now()
  let newItem = {
    index: store.getters.nextItemIndex,
    createOn: stamp,
    createBy: {
      uid: store.getters.currentUserInfo.uid,
      name: store.getters.currentUserInfo.name
    },
    priceInfo: {
      list: '',
      surcharge: '',
      costFactor: '',
      discount: ''
    },
    lastEditOn: null,
    lastEditBy: null,
    id: localDB.getRandomId('items'),
    name: addItemRequest.newItemName,
    rf: addItemRequest.RF,
    templateName: formTemplate.templateName,
    templateId: addItemRequest.templateId,
    folderId: addItemRequest.folderId,
    projectId: store.state.currentProject.id,
    images: [],
    open: true,
    version: 0
  }
  // assemble the item questions
  let questions = []
  for (let questionSnap of formTemplate.questionList) {
    let question = store.state.formQuestions.find(question => { return question.id === questionSnap.questionId })
    if (question === undefined) {
      throw new Error('There is an issue with the template selected [code=1]')
    }
    question.required = questionSnap.required
    question.filled = false
    questions.push(question)
  }
  newItem.questions = questions
  return newItem
}

function calcItemList () {
  let fullItemList = store.getters.fullItemList
  if (store.state.currentFolder === null) {
    // filter the item at root folder
    return fullItemList.filter(item => {
      return item.folderId === null
    }).sort((itemA, itemB) => {
      return itemA.index - itemB.index
    })
  } else if (store.state.currentFolder.id) {
    // filter the item at current folder
    return fullItemList.filter(item => {
      return item.folderId === store.state.currentFolder.id
    }).sort((itemA, itemB) => {
      return itemA.index - itemB.index
    })
  } else {
    return []
  }
}

function calcOfflineItemList () {
  let fullOfflineItemList = store.getters.fullOfflineItemList
  if (store.state.currentFolder === null) {
    // filter the item at root folder
    return fullOfflineItemList.filter(item => {
      return item.folderId === null
    }).sort((itemA, itemB) => {
      return itemA.index - itemB.index
    })
  } else if (store.state.currentFolder.id) {
    // filter the item at current folder
    return fullOfflineItemList.filter(item => {
      return item.folderId === store.state.currentFolder.id
    }).sort((itemA, itemB) => {
      return itemA.index - itemB.index
    })
  } else {
    return []
  }
}

function calcNextItemIndex () {
  return store.getters.itemList.length + store.getters.offlineItemList.length + 1
}
